import React from "react";
import Box from "@/ui/atoms/Box";
import { Icon, IconBox, Tooltip } from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";
import { isTruthy } from "@/utils/guards";

type ErrorType = "missingMapping" | "dataOutlier" | "llmValidation";

const superscript = {
    dataOutlier:
        "A modified z-score is used to identify outliers in a dataset. " +
        "For each account, it measures how far each non-zero value sits from " +
        "the median, scaled by the median absolute deviation (MAD) of that " +
        "account's values. Values scoring beyond the threshold are flagged, " +
        "with the statistic calculated separately for each account.",
};

const checkTypeLabels: Record<string, string> = {
    income_total: "Income Total Mismatch",
    expense_total: "Expense Total Mismatch",
    noi: "NOI Mismatch",
    leaf_total: "Subtotal Mismatch",
    net_income: "Net Income Mismatch",
};

const userActionLabels: Record<string, string> = {
    flip_sign: "Flip sign",
    change_row_type: "Change row type",
    change_mapping: "Change mapping",
    correct_value: "Correct value",
    ignore_row: "Ignore row",
    ignore_column: "Ignore column",
    check_source_document: "Check source",
    none: "Review",
};

const checkTypeDescriptions: Record<string, string> = {
    income_total:
        "The sum of all income line items does not match the Total Income row.",
    expense_total:
        "The sum of all expense line items does not match the Total Expenses row.",
    noi: "Net Operating Income (Total Income minus Total Expenses) does not match the NOI row.",
    leaf_total:
        "The sum of account rows directly above a subtotal does not match the subtotal value.",
    net_income:
        "Net Income (NOI minus non-operating expenses) does not match the Net Income row.",
};

const cardLeftBorderColor = {
    warning: "var(--proda-yellow)",
    error: "var(--proda-red)",
    resolved: "var(--proda-green-120)",
    dismissed: "var(--proda-white-150)",
};

export const testIds = {
    title: "ExtractionsErrorList-title",
    result: "ExtractionsErrorList-result",
    selectedResult: "ExtractionsErrorList-selectedResult",
    nextErrorGroup: "ExtractionsErrorList-nextErrorGroup",
    prevErrorGroup: "ExtractionsErrorList-prevErrorGroup",
    dismissGroup: "ExtractionsErrorList-dismissGroup",
};

export type ErrorRow = {
    rowId: number | string;
    colId: string;
    periodId?: number;
    columnName?: string;
    accountName: string;
    errorType: ErrorType;
    errorValue?: number;
    severity: "warning" | "error";
    threshold?: number;
    direction?: "small" | "large";
    resolved?: boolean;
    dismissed?: boolean;
    checkType?: string;
    expected?: string;
    actual?: string;
    diff?: string;
    diagnosisReason?: string;
    diagnosisFix?: string | null;
    diagnosisUserAction?: string;
    diagnosisTargetRowIds?: Array<number | string>;
    diagnosisCheckIdentities?: string[];
    diagnosisDismissed?: boolean;
};

export type ExtractionsErrorListProps = JSX.IntrinsicAttributes & {
    errors: { [key: string]: ErrorRow[] };
    highlightedCell?: {
        rowId: number | string | null;
        colId: string | null;
    } | null;
    onViewError: (rowId: number | string | null, colId: string | null) => void;
    onDismissWarnings?: (
        warnings: {
            rowId: number;
            periodId: number;
            accountLabel: string;
        }[],
    ) => void;
    onHighlightTargetRows?: (rowIds: Array<number | string>) => void;
    onDismissDiagnosis?: (
        groups: { checkIdentities: string[]; dismissed: boolean }[],
    ) => void;
};

const ExtractionsErrorList: React.FC<ExtractionsErrorListProps> = ({
    errors,
    highlightedCell: highlightedCellFromProps,
    onViewError,
    onDismissWarnings,
    onHighlightTargetRows,
    onDismissDiagnosis,
}) => {
    const sortedErrors = Object.fromEntries(
        Object.entries(errors)
            .filter(([, errorGroup]) => isTruthy(errorGroup[0]?.accountName))
            .toSorted(([, groupA], [, groupB]) => {
                const isGroupADismissed = groupA.every(
                    error => error.dismissed === true,
                );
                const isGroupBDismissed = groupB.every(
                    error => error.dismissed === true,
                );
                if (isGroupADismissed && !isGroupBDismissed) return 1;
                if (!isGroupADismissed && isGroupBDismissed) return -1;
                return 0;
            }),
    );
    const thereAreErrors = Object.keys(sortedErrors).length > 0;
    const makeSureHighlightedCellIsIncludedInTheListOfErrors = () => {
        if (!thereAreErrors) return null;
        const errorGroup = Object.values(sortedErrors).find(errorGroup => {
            const rowId = errorGroup[0]?.rowId;
            return rowId === highlightedCellFromProps?.rowId;
        });
        return errorGroup?.find(
            error => error.colId === highlightedCellFromProps?.colId,
        );
    };

    const highlightedCell =
        makeSureHighlightedCellIsIncludedInTheListOfErrors();

    const groupKeys = Object.keys(sortedErrors);

    const previousGroupToHighlightedOne = () => {
        if (!highlightedCell) return groupKeys.at(-1);
        const currentGroupIndex = groupKeys.findIndex(rowId => {
            const rowIdFromGroup = rowId.split(",")[0];
            return rowIdFromGroup === highlightedCell.rowId.toString();
        });
        const previousGroupIndex =
            (currentGroupIndex - +1 + groupKeys.length) % groupKeys.length;
        return groupKeys[previousGroupIndex];
    };

    const nextGroupToHighlightedOne = () => {
        if (!highlightedCell) return groupKeys[0];
        const currentGroupIndex = groupKeys.findIndex(rowId => {
            const rowIdFromGroup = rowId.split(",")[0];
            return rowIdFromGroup === highlightedCell.rowId.toString();
        });
        const nextGroupIndex = (currentGroupIndex + 1) % groupKeys.length;
        return groupKeys[nextGroupIndex];
    };

    const handleClickToPreviousGroup = () => {
        const previewGroupKey = previousGroupToHighlightedOne();
        const rowId = previewGroupKey?.split(",")[0];
        if (
            previewGroupKey !== undefined &&
            rowId !== undefined &&
            sortedErrors[previewGroupKey]?.[0]?.colId !== undefined
        ) {
            onViewError(rowId, sortedErrors[previewGroupKey][0].colId);
        }
    };

    const handleClickToNextGroup = () => {
        const nextGroupKey = nextGroupToHighlightedOne();
        const rowId = nextGroupKey?.split(",")[0];
        if (
            nextGroupKey !== undefined &&
            rowId !== undefined &&
            sortedErrors[nextGroupKey]?.[0]?.colId !== undefined
        ) {
            onViewError(rowId, sortedErrors[nextGroupKey][0].colId);
        }
    };

    const nextDisabled = !thereAreErrors;
    const previousDisabled = !thereAreErrors;

    const formatNumber = (value?: number) => {
        if (value === undefined) return "";
        if (value === 0) return "0.00";
        return value.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };

    const formatValidationNumber = (value?: string) => {
        if (value === undefined || value === "") return "";
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderErrorCardContent = (
        errorGroup: ErrorRow[],
        isSelected: boolean,
    ) => {
        const groupResolved = errorGroup.every(
            error => error.resolved === true,
        );

        if (errorGroup[0]?.errorType === "llmValidation") {
            const isError = errorGroup[0]?.severity === "error";
            const checkLabel =
                checkTypeLabels[errorGroup[0]?.checkType ?? ""] ??
                "Validation Issue";
            // Distinct diagnosis groups in this error group, for the
            // eye-slash dismiss toggle.
            const seenKeys = new Set<string>();
            const diagnosisGroups = errorGroup.flatMap(error => {
                const checkIdentities = error.diagnosisCheckIdentities ?? [];
                if (checkIdentities.length === 0) return [];
                const key = JSON.stringify(checkIdentities);
                if (seenKeys.has(key)) return [];
                seenKeys.add(key);
                return [
                    {
                        checkIdentities,
                        dismissed: error.diagnosisDismissed === true,
                    },
                ];
            });
            const hasDiagnosis = diagnosisGroups.length > 0;
            const diagnosisDismissed =
                hasDiagnosis && diagnosisGroups.every(group => group.dismissed);
            return (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem",
                        }}
                    >
                        <Typography
                            variant="body3"
                            sx={{
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                minWidth: 0,
                                flex: 1,
                                overflow: "hidden",
                                color: diagnosisDismissed
                                    ? cardLeftBorderColor["dismissed"]
                                    : undefined,
                            }}
                        >
                            <IconBox mr={0.5} style={{ flexShrink: 0 }}>
                                <Icon
                                    icon={
                                        isError
                                            ? "circle-exclamation"
                                            : "triangle-exclamation"
                                    }
                                    size={12}
                                    color={
                                        diagnosisDismissed
                                            ? cardLeftBorderColor["dismissed"]
                                            : isError
                                              ? "var(--proda-red)"
                                              : undefined
                                    }
                                />
                            </IconBox>
                            <span
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {checkLabel}
                                {errorGroup[0]?.checkType === "leaf_total" &&
                                    errorGroup[0]?.accountName && (
                                        <span
                                            style={{
                                                fontWeight: 400,
                                                marginLeft: "0.25rem",
                                                color: "var(--proda-white-150)",
                                            }}
                                        >
                                            — {errorGroup[0].accountName}
                                        </span>
                                    )}
                            </span>
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                                gap: "0.25rem",
                            }}
                        >
                            <Tooltip
                                style={{ display: "inline-flex" }}
                                placement={"right"}
                                content={
                                    checkTypeDescriptions[
                                        errorGroup[0]?.checkType ?? ""
                                    ] ?? checkTypeDescriptions["leaf_total"]
                                }
                            >
                                <IconBox>
                                    <Icon icon="circle-info" size="sm" />
                                </IconBox>
                            </Tooltip>
                            {onDismissDiagnosis && hasDiagnosis && (
                                <IconBox
                                    style={{ cursor: "pointer" }}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onDismissDiagnosis(
                                            diagnosisGroups.map(group => ({
                                                checkIdentities:
                                                    group.checkIdentities,
                                                dismissed: !diagnosisDismissed,
                                            })),
                                        );
                                    }}
                                >
                                    <Icon
                                        icon={
                                            diagnosisDismissed
                                                ? "eye"
                                                : "eye-slash"
                                        }
                                        size={12}
                                        color="var(--proda-black-10)"
                                    />
                                </IconBox>
                            )}
                        </Box>
                    </Box>
                    {isSelected && (
                        <>
                            {errorGroup.map(error => (
                                <Box
                                    key={error.colId}
                                    sx={{
                                        backgroundColor: diagnosisDismissed
                                            ? "var(--proda-white-110)"
                                            : "var(--proda-white-l95)",
                                        padding: "8px",
                                        borderRadius: "4px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "0.25rem",
                                        cursor: "pointer",
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onViewError(error.rowId, error.colId);
                                    }}
                                >
                                    <Typography
                                        variant="body3"
                                        sx={{
                                            fontWeight: 700,
                                            color: diagnosisDismissed
                                                ? cardLeftBorderColor[
                                                      "dismissed"
                                                  ]
                                                : undefined,
                                        }}
                                    >
                                        {error.columnName}
                                    </Typography>
                                    <Typography
                                        variant="body3"
                                        sx={{
                                            color: diagnosisDismissed
                                                ? cardLeftBorderColor[
                                                      "dismissed"
                                                  ]
                                                : undefined,
                                        }}
                                    >
                                        Expected:{" "}
                                        {formatValidationNumber(error.expected)}{" "}
                                        | Actual:{" "}
                                        {formatValidationNumber(error.actual)}
                                    </Typography>
                                    <Typography
                                        variant="body3"
                                        sx={{
                                            color: diagnosisDismissed
                                                ? cardLeftBorderColor[
                                                      "dismissed"
                                                  ]
                                                : isError
                                                  ? "var(--proda-red)"
                                                  : "var(--proda-yellow)",
                                        }}
                                    >
                                        Diff:{" "}
                                        {formatValidationNumber(error.diff)}
                                    </Typography>
                                </Box>
                            ))}
                            {(() => {
                                const seen = new Set<string>();
                                const uniqueDiagnoses = errorGroup
                                    .filter(
                                        error =>
                                            error.diagnosisFix != null ||
                                            error.diagnosisReason != null,
                                    )
                                    .filter(error => {
                                        const key = `${error.diagnosisUserAction ?? ""}|${error.diagnosisFix ?? ""}`;
                                        if (seen.has(key)) return false;
                                        seen.add(key);
                                        return true;
                                    });
                                if (uniqueDiagnoses.length === 0) return null;
                                return (
                                    <>
                                        <Typography
                                            variant="body3"
                                            sx={{
                                                fontWeight: 700,
                                                marginTop: "0.25rem",
                                                color: diagnosisDismissed
                                                    ? cardLeftBorderColor[
                                                          "dismissed"
                                                      ]
                                                    : "var(--proda-black-10)",
                                            }}
                                        >
                                            Suggested actions
                                        </Typography>
                                        {uniqueDiagnoses.map(
                                            (diagnosis, index) => {
                                                const actionLabel =
                                                    userActionLabels[
                                                        diagnosis.diagnosisUserAction ??
                                                            ""
                                                    ] ?? "Suggested fix";
                                                const targetRowIds =
                                                    diagnosis.diagnosisTargetRowIds ??
                                                    [];
                                                const clickable =
                                                    targetRowIds.length > 0 &&
                                                    !!onHighlightTargetRows;
                                                const isDismissed =
                                                    diagnosis.diagnosisDismissed ===
                                                    true;
                                                return (
                                                    <Box
                                                        key={`diag-${index}`}
                                                        sx={{
                                                            backgroundColor:
                                                                isDismissed
                                                                    ? "var(--proda-white-110)"
                                                                    : "var(--proda-white-l95)",
                                                            padding: "6px 8px",
                                                            borderRadius: "4px",
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: "0.5rem",
                                                            borderLeft:
                                                                isDismissed
                                                                    ? "3px solid var(--proda-white-150)"
                                                                    : "3px solid var(--proda-blue)",
                                                            cursor: clickable
                                                                ? "pointer"
                                                                : "default",
                                                        }}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            if (clickable) {
                                                                onHighlightTargetRows?.(
                                                                    targetRowIds,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                fontSize:
                                                                    "11px",
                                                                fontWeight: 700,
                                                                color: isDismissed
                                                                    ? "var(--proda-white-150)"
                                                                    : "var(--proda-blue)",
                                                                backgroundColor:
                                                                    isDismissed
                                                                        ? "var(--proda-white-110)"
                                                                        : "var(--proda-blue-l95)",
                                                                padding:
                                                                    "2px 6px",
                                                                borderRadius:
                                                                    "3px",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {actionLabel}
                                                        </Box>
                                                        <Typography
                                                            variant="body3"
                                                            sx={{
                                                                flex: 1,
                                                                color: isDismissed
                                                                    ? cardLeftBorderColor[
                                                                          "dismissed"
                                                                      ]
                                                                    : undefined,
                                                            }}
                                                        >
                                                            {diagnosis.diagnosisFix ??
                                                                diagnosis.diagnosisReason}
                                                        </Typography>
                                                        {diagnosis.diagnosisReason !=
                                                            null &&
                                                            diagnosis.diagnosisFix !=
                                                                null && (
                                                                <Tooltip
                                                                    placement="right"
                                                                    content={
                                                                        diagnosis.diagnosisReason
                                                                    }
                                                                >
                                                                    <IconBox>
                                                                        <Icon
                                                                            icon="circle-info"
                                                                            size="sm"
                                                                        />
                                                                    </IconBox>
                                                                </Tooltip>
                                                            )}
                                                    </Box>
                                                );
                                            },
                                        )}
                                    </>
                                );
                            })()}
                        </>
                    )}
                </>
            );
        }

        if (errorGroup[0]?.severity === "warning") {
            return (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color:
                                errorGroup[0]?.dismissed === true
                                    ? cardLeftBorderColor["dismissed"]
                                    : undefined,
                        }}
                    >
                        <Typography
                            variant="body3"
                            sx={{
                                fontWeight: 700,
                                color:
                                    errorGroup[0]?.dismissed === true
                                        ? cardLeftBorderColor["dismissed"]
                                        : undefined,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <IconBox mr={0.5}>
                                <Icon
                                    icon={
                                        groupResolved
                                            ? "circle-check"
                                            : "triangle-exclamation"
                                    }
                                    size={12}
                                    color={
                                        groupResolved
                                            ? "var(--proda-green-120)"
                                            : undefined
                                    }
                                />
                            </IconBox>
                            {errorGroup.length} {errorGroup[0]?.accountName}
                        </Typography>
                        {onDismissWarnings && (
                            <IconBox
                                style={{ cursor: "pointer" }}
                                data-testid={testIds.dismissGroup}
                                onClick={e => {
                                    e.stopPropagation();
                                    const willDismiss =
                                        errorGroup[0]?.dismissed !== true;
                                    onDismissWarnings(
                                        errorGroup.map(error => ({
                                            rowId: error.rowId as number,
                                            periodId: error.periodId as number,
                                            accountLabel: error.accountName,
                                        })),
                                    );
                                    if (willDismiss && isSelected) {
                                        onViewError(null, null);
                                    }
                                }}
                            >
                                <Icon
                                    icon={
                                        errorGroup[0]?.dismissed === true
                                            ? "eye"
                                            : "eye-slash"
                                    }
                                    size={12}
                                    color="var(--proda-black-10)"
                                />
                            </IconBox>
                        )}
                    </Box>
                    {isSelected && (
                        <>
                            {errorGroup.map(error => {
                                return (
                                    <Box
                                        key={error.colId}
                                        sx={{
                                            backgroundColor:
                                                errorGroup[0]?.dismissed ===
                                                true
                                                    ? "var(--proda-white-110)"
                                                    : "var(--proda-white-l95)",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            justifyContent: "space-between",
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onViewError(
                                                error.rowId,
                                                error.colId,
                                            );
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body3"
                                                sx={{
                                                    fontWeight: 700,
                                                    color:
                                                        error.dismissed === true
                                                            ? cardLeftBorderColor[
                                                                  "dismissed"
                                                              ]
                                                            : undefined,
                                                }}
                                            >
                                                {error.columnName}
                                            </Typography>
                                            <Typography
                                                variant="body3"
                                                sx={{
                                                    color:
                                                        error.dismissed === true
                                                            ? cardLeftBorderColor[
                                                                  "dismissed"
                                                              ]
                                                            : undefined,
                                                }}
                                            >
                                                :{" "}
                                                {error.direction
                                                    ? `Value is out of the expected range, ${error.direction === "large" ? "larger" : "smaller"} than ${formatNumber(error.threshold)}`
                                                    : "Multiple sources of conflicting column data"}
                                                {superscript.dataOutlier && (
                                                    <>
                                                        <Tooltip
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                display:
                                                                    "inline",
                                                            }}
                                                            placement={"right"}
                                                            content={
                                                                superscript.dataOutlier
                                                            }
                                                        >
                                                            <IconBox ml={1}>
                                                                <Icon
                                                                    icon="circle-info"
                                                                    size="sm"
                                                                />
                                                            </IconBox>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Typography>
                                        </Box>
                                        <IconBox
                                            style={{
                                                visibility:
                                                    error.resolved === false ||
                                                    error.resolved === undefined
                                                        ? "hidden"
                                                        : undefined,
                                            }}
                                        >
                                            <Icon
                                                icon="circle-check"
                                                size={12}
                                                color="var(--proda-green-120)"
                                            />
                                        </IconBox>
                                    </Box>
                                );
                            })}
                        </>
                    )}
                </>
            );
        } else {
            return (
                <>
                    <Typography variant="body3" sx={{ fontWeight: 700 }}>
                        <IconBox mr={0.5}>
                            <Icon
                                icon={
                                    groupResolved
                                        ? "circle-check"
                                        : "circle-exclamation"
                                }
                                color={
                                    groupResolved
                                        ? "var(--proda-green-120)"
                                        : "var(--proda-red)"
                                }
                                size={12}
                            />
                        </IconBox>
                        {errorGroup[0]?.errorType === "missingMapping"
                            ? "Missing mapping"
                            : `${errorGroup.length} ${
                                  errorGroup[0]?.accountName !== ""
                                      ? errorGroup[0]?.accountName
                                      : "-"
                              }`}
                    </Typography>
                    {isSelected && (
                        <>
                            {errorGroup.map(error => {
                                return (
                                    <Box
                                        key={error.colId}
                                        sx={{
                                            backgroundColor:
                                                "var(--proda-white-l95)",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            justifyContent: "space-between",
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onViewError(
                                                error.rowId,
                                                error.colId,
                                            );
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body3"
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Account Mapping
                                            </Typography>
                                            <Typography variant="body3">
                                                :{" "}
                                                {error.errorType ===
                                                "missingMapping"
                                                    ? "Missing Value"
                                                    : "-"}
                                            </Typography>
                                        </Box>
                                        <IconBox
                                            style={{
                                                visibility:
                                                    error.resolved === false ||
                                                    error.resolved === undefined
                                                        ? "hidden"
                                                        : undefined,
                                            }}
                                        >
                                            <Icon
                                                icon="circle-check"
                                                size={12}
                                                color="var(--proda-green-120)"
                                            />
                                        </IconBox>
                                    </Box>
                                );
                            })}
                        </>
                    )}
                </>
            );
        }
    };
    const totalErrorCount = Object.values(sortedErrors).reduce(
        (sum, group) =>
            sum +
            group.filter(
                error =>
                    error.severity === "error" &&
                    (error.resolved === false || error.resolved === undefined),
            ).length,
        0,
    );
    const totalWarningsCount = Object.values(sortedErrors).reduce(
        (sum, group) =>
            sum +
            group.filter(
                error =>
                    error.severity === "warning" &&
                    (error.dismissed === false ||
                        error.dismissed === undefined),
            ).length,
        0,
    );

    return (
        <>
            <Box sx={{ width: "100%" }}>
                <Typography variant="h3" sx={{ mb: "8px" }}>
                    Errors & Warnings
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            fontFamily: "Noto Sans",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "var(--proda-black-10)",
                        }}
                        data-testid={testIds.title}
                    >
                        {thereAreErrors
                            ? `${totalErrorCount} errors | ${totalWarningsCount} warnings`
                            : "No errors found"}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconBox
                            style={{
                                cursor: previousDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                color: previousDisabled
                                    ? "var(--proda-white-130)"
                                    : "var(--proda-black)",
                                transform: "rotate(90deg)",
                            }}
                            mr={1}
                            data-testid={testIds.prevErrorGroup}
                            onClick={
                                previousDisabled
                                    ? undefined
                                    : () => handleClickToPreviousGroup()
                            }
                        >
                            <Icon icon="angle-left" />
                        </IconBox>
                        <IconBox
                            style={{
                                cursor: nextDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                color: nextDisabled
                                    ? "var(--proda-white-130)"
                                    : "var(--proda-black)",
                                transform: "rotate(-90deg)",
                            }}
                            data-testid={testIds.nextErrorGroup}
                            onClick={
                                nextDisabled
                                    ? undefined
                                    : () => handleClickToNextGroup()
                            }
                        >
                            <Icon icon="angle-left" />
                        </IconBox>
                    </Box>
                </Box>

                {Object.entries(sortedErrors).map(
                    ([rowId, errorGroup]: [
                        rowId: string,
                        errorGroup: ErrorRow[],
                    ]) => {
                        const isSelected =
                            highlightedCell?.rowId === errorGroup[0]?.rowId;
                        return (
                            <Box
                                key={rowId}
                                sx={{
                                    borderLeft: "8px solid",
                                    borderLeftColor:
                                        cardLeftBorderColor[
                                            errorGroup.every(
                                                error =>
                                                    error.dismissed === true,
                                            )
                                                ? "dismissed"
                                                : errorGroup.every(
                                                        error =>
                                                            error.resolved ===
                                                            true,
                                                    )
                                                  ? "resolved"
                                                  : errorGroup[0]?.severity ??
                                                    "warning"
                                        ],
                                    borderRadius: "0.25rem",
                                    boxShadow:
                                        "1px 2px 6px 0px rgba(6, 10, 22, 0.25)",
                                    marginBottom: "1rem",
                                    padding: "1rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.5rem",
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    if (errorGroup[0]?.rowId === undefined)
                                        return;
                                    if (
                                        highlightedCell?.rowId ===
                                        errorGroup[0]?.rowId
                                    ) {
                                        onViewError(null, null);
                                    } else {
                                        onViewError(
                                            errorGroup[0].rowId,
                                            errorGroup[0].colId,
                                        );
                                    }
                                }}
                                data-testid={
                                    isSelected
                                        ? testIds.selectedResult
                                        : testIds.result
                                }
                            >
                                {renderErrorCardContent(errorGroup, isSelected)}
                            </Box>
                        );
                    },
                )}
            </Box>
        </>
    );
};

export default ExtractionsErrorList;
